import * as React from "react";

import { Calendar } from "./calendar";
import { cn } from "./cn";
import {
  compareDates,
  DATE_PRESET_LABEL,
  DATE_RANGE_PRESETS,
  type DatePreset,
  type DateRange,
  type DateRangeChoice,
  formatDateRange,
  type IsoDate,
  type IsoMonth,
  matchDatePreset,
  monthOf,
  parseTypedDate,
  rangeLabel,
  resolveDatePreset,
  shiftMonth,
  todayIso,
} from "./date-range";
import {
  ClearButton,
  PickerClose,
  type PickerSize,
  PickerSurface,
  PickerTrigger,
  usePickerLayout,
} from "./picker-shell";
import { buttonRecipe, focusRing, inputVariants } from "./recipes";

type DateRangePickerProps = {
  /** `YYYY-MM-DD` ends; an empty end is open, both empty is all time. */
  value: DateRange;
  /**
   * The preset the caller knows was picked (a URL `period`). Without it the
   * trigger names whichever preset the dates equal.
   */
  preset?: DateRangeChoice;
  onChange: (range: DateRange, preset: DateRangeChoice) => void;
  /** The preset menu, top to bottom. Custom is always offered last. */
  presets?: readonly DatePreset[];
  /** Shows the × that clears an optional filter. */
  onClear?: () => void;
  /** Today, `YYYY-MM-DD`; injected by tests. */
  today?: IsoDate;
  /** The trigger's accessible name. */
  label?: string;
  /** Trigger text when no range is set. */
  placeholder?: string;
  size?: PickerSize;
  min?: IsoDate;
  max?: IsoDate;
  disabled?: boolean;
  id?: string;
  className?: string;
};

/**
 * One button that opens presets, a range calendar and typed dates. Nothing
 * reaches the caller until Apply; Cancel or Escape leave the value alone.
 */
function DateRangePicker({
  value,
  preset,
  onChange,
  presets = DATE_RANGE_PRESETS,
  onClear,
  today: todayProp,
  label = "Date range",
  placeholder = "All dates",
  size,
  min,
  max,
  disabled,
  id,
  className,
}: DateRangePickerProps) {
  const today = todayProp ?? todayIso();
  const [open, setOpen] = React.useState(false);
  const { phone, wide } = usePickerLayout();

  const empty = !value.from && !value.to;
  const choice =
    preset && preset !== "custom"
      ? preset
      : matchDatePreset(value, today, presets);
  const text =
    empty && !presets.includes("all_time")
      ? placeholder
      : rangeLabel(value, today, presets, choice);
  const clearable = !!onClear && !empty;

  return (
    <div
      data-slot="date-range-picker"
      className={cn("relative inline-flex max-w-full min-w-0", className)}
    >
      <PickerSurface
        open={open}
        onOpenChange={setOpen}
        phone={phone}
        title={label}
        focusOnOpen="[role=option][tabindex='0']"
        trigger={
          <PickerTrigger
            id={id}
            text={text}
            placeholder={empty}
            size={size}
            clearable={clearable}
            disabled={disabled}
            aria-label={`${label}: ${text}`}
          />
        }
      >
        {open ? (
          <DateRangePanel
            value={value}
            choice={choice}
            presets={presets}
            today={today}
            months={phone || !wide ? 1 : 2}
            phone={phone}
            min={min}
            max={max}
            onApply={(range, picked) => {
              onChange(range, picked);
              setOpen(false);
            }}
          />
        ) : null}
      </PickerSurface>
      {clearable ? (
        <ClearButton label={`Clear ${label.toLowerCase()}`} onClear={onClear} />
      ) : null}
    </div>
  );
}

type DateRangePanelProps = {
  value: DateRange;
  choice: DateRangeChoice;
  presets: readonly DatePreset[];
  today: IsoDate;
  months: 1 | 2;
  phone: boolean;
  min?: IsoDate;
  max?: IsoDate;
  onApply: (range: DateRange, preset: DateRangeChoice) => void;
};

/** The first month to show so the range sits on screen, start on the left. */
function monthFor(range: DateRange, today: IsoDate, months: 1 | 2): IsoMonth {
  const end = range.to || range.from;
  if (!end) return shiftMonth(monthOf(today), 1 - months);
  const start = range.from || end;
  if (months === 1)
    return monthOf(end === range.to && !range.from ? end : start);
  // Both ends fit: start on the left. Otherwise keep the end in view.
  return monthOf(end) <= shiftMonth(monthOf(start), 1)
    ? monthOf(start)
    : shiftMonth(monthOf(end), -1);
}

type FieldState = { text: string; error: string };
const HINT = "YYYY-MM-DD";

function DateRangePanel({
  value,
  choice: initialChoice,
  presets,
  today,
  months,
  phone,
  min,
  max,
  onApply,
}: DateRangePanelProps) {
  const [draft, setDraft] = React.useState<DateRange>(value);
  const [choice, setChoice] = React.useState<DateRangeChoice>(initialChoice);
  const [anchor, setAnchor] = React.useState<IsoDate | null>(null);
  const [month, setMonth] = React.useState<IsoMonth>(() =>
    monthFor(value, today, months),
  );
  const [fromField, setFromField] = React.useState<FieldState>({
    text: value.from,
    error: "",
  });
  const [toField, setToField] = React.useState<FieldState>({
    text: value.to,
    error: "",
  });
  const [orderError, setOrderError] = React.useState("");
  const fromInput = React.useRef<HTMLInputElement>(null);
  const ids = React.useId();

  // Re-window when the panel changes between one month and two.
  const lastMonths = React.useRef(months);
  React.useEffect(() => {
    if (lastMonths.current === months) return;
    lastMonths.current = months;
    setMonth(monthFor(draft, today, months));
  }, [months, draft, today]);

  const options: DateRangeChoice[] = [...presets, "custom"];

  const show = (range: DateRange) => {
    setDraft(range);
    setFromField({ text: range.from, error: "" });
    setToField({ text: range.to, error: "" });
    setOrderError("");
  };

  const pickPreset = (next: DateRangeChoice) => {
    setChoice(next);
    setAnchor(null);
    if (next === "custom") {
      fromInput.current?.focus();
      return;
    }
    const range = resolveDatePreset(next, today);
    show(range);
    if (range.from || range.to) setMonth(monthFor(range, today, months));
  };

  const pickDay = (date: IsoDate) => {
    if (!anchor) {
      setAnchor(date);
      setChoice("custom");
      show({ from: date, to: "" });
      return;
    }
    const range =
      compareDates(anchor, date) <= 0
        ? { from: anchor, to: date }
        : { from: date, to: anchor };
    setAnchor(null);
    show(range);
    setChoice(matchDatePreset(range, today, presets));
  };

  /** A typed end as a date ("" when blank), or the sentence that says how to fix it. */
  const check = (text: string): { date: IsoDate } | { error: string } => {
    const trimmed = text.trim();
    if (!trimmed) return { date: "" };
    const parsed = parseTypedDate(trimmed);
    if (!parsed)
      return { error: `Type the date as ${HINT}, for example ${today}.` };
    if ((min && parsed < min) || (max && parsed > max))
      return {
        error: `Pick a date from ${min ?? "any date"} to ${max ?? "any date"}.`,
      };
    return { date: parsed };
  };

  const commitField = (end: "from" | "to") => {
    const field = end === "from" ? fromField : toField;
    const setField = end === "from" ? setFromField : setToField;
    const result = check(field.text);
    if ("error" in result) {
      setField({ text: field.text, error: result.error });
      return;
    }
    setField({ text: result.date, error: "" });
    if (result.date === draft[end]) return;
    const next = { ...draft, [end]: result.date };
    setAnchor(null);
    setDraft(next);
    setOrderError("");
    setChoice(matchDatePreset(next, today, presets));
    if (result.date) setMonth(monthFor(next, today, months));
  };

  const apply = () => {
    const from = check(fromField.text);
    const to = check(toField.text);
    if ("error" in from || "error" in to) {
      if ("error" in from) setFromField({ ...fromField, error: from.error });
      if ("error" in to) setToField({ ...toField, error: to.error });
      return;
    }
    // A start still waiting for its end is a one-day range.
    const range: DateRange = anchor
      ? { from: anchor, to: anchor }
      : { from: from.date, to: to.date };
    if (range.from && range.to && compareDates(range.from, range.to) > 0) {
      setOrderError(
        "The end date is before the start date. Pick a later end date.",
      );
      return;
    }
    const named = resolveDatePreset(
      choice === "custom" ? "all_time" : choice,
      today,
    );
    const keepsChoice =
      choice !== "custom" && named.from === range.from && named.to === range.to;
    onApply(
      range,
      keepsChoice ? choice : matchDatePreset(range, today, presets),
    );
  };

  const onPresetKey = (event: React.KeyboardEvent, index: number) => {
    const horizontal = phone;
    const nextKey = horizontal ? "ArrowRight" : "ArrowDown";
    const prevKey = horizontal ? "ArrowLeft" : "ArrowUp";
    let target = -1;
    if (event.key === nextKey) target = Math.min(index + 1, options.length - 1);
    else if (event.key === prevKey) target = Math.max(index - 1, 0);
    else if (event.key === "Home") target = 0;
    else if (event.key === "End") target = options.length - 1;
    if (target < 0) return;
    event.preventDefault();
    const list = event.currentTarget.parentElement;
    list?.querySelectorAll<HTMLButtonElement>("[role=option]")[target]?.focus();
  };

  const summary = anchor
    ? `${formatDateRange({ from: anchor, to: anchor })} – pick an end date`
    : formatDateRange(draft);

  const dateField = (
    end: "from" | "to",
    field: FieldState,
    setField: (next: FieldState) => void,
  ) => {
    const inputId = `${ids}-${end}`;
    const hintId = `${inputId}-hint`;
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-muted-foreground"
        >
          {end === "from" ? "From" : "To"}
        </label>
        <input
          id={inputId}
          ref={end === "from" ? fromInput : undefined}
          inputMode="numeric"
          autoComplete="off"
          placeholder={HINT}
          value={field.text}
          aria-invalid={field.error ? true : undefined}
          aria-describedby={hintId}
          onChange={(event) =>
            setField({ text: event.target.value, error: "" })
          }
          onBlur={() => commitField(end)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitField(end);
            }
          }}
          className={cn(
            inputVariants({ size: "sm" }),
            "font-mono tabular-nums",
          )}
        />
        <p
          id={hintId}
          className={cn(
            "min-h-4 text-xs leading-4",
            field.error ? "text-danger" : "text-muted-foreground",
          )}
        >
          {field.error || HINT}
        </p>
      </div>
    );
  };

  const activeIndex = Math.max(options.indexOf(choice), 0);

  return (
    <div
      data-slot="date-range-panel"
      className={cn("flex text-sm", phone ? "flex-col gap-3" : "flex-row")}
    >
      <div
        role="listbox"
        aria-label="Presets"
        aria-orientation={phone ? "horizontal" : "vertical"}
        className={cn(
          phone
            ? "-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]"
            : "flex w-40 shrink-0 flex-col gap-0.5 border-r border-border p-2",
        )}
      >
        {options.map((option, index) => {
          const active = option === choice;
          return (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={active}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => pickPreset(option)}
              onKeyDown={(event) => onPresetKey(event, index)}
              className={cn(
                "shrink-0 rounded-control text-left whitespace-nowrap transition-colors",
                focusRing,
                phone
                  ? "h-8 rounded-pill border border-border px-3 text-sm"
                  : "flex h-8 items-center justify-between px-2.5",
                active
                  ? phone
                    ? "border-primary bg-accent font-medium text-accent-foreground"
                    : "bg-accent font-medium text-accent-foreground"
                  : "text-foreground hover:bg-muted",
              )}
            >
              {DATE_PRESET_LABEL[option]}
              {active && !phone ? (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  className="size-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3.5 8.5 3 3 6-7" />
                </svg>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className={cn("flex min-w-0 flex-col gap-3", !phone && "p-4")}>
        <Calendar
          month={month}
          onMonthChange={setMonth}
          numberOfMonths={months}
          selected={draft}
          anchor={anchor}
          onSelect={pickDay}
          today={today}
          min={min}
          max={max}
        />
        <div className="flex gap-3">
          {dateField("from", fromField, setFromField)}
          {dateField("to", toField, setToField)}
        </div>
        <div
          className={cn(
            "flex items-center gap-2 border-t border-border pt-3",
            phone && "sticky bottom-0 -mx-4 -mb-4 bg-card px-4 pb-4",
          )}
        >
          <p
            aria-live="polite"
            className={cn(
              "mr-auto min-w-0 truncate text-xs",
              orderError ? "text-danger" : "text-muted-foreground",
            )}
          >
            {orderError || summary}
          </p>
          <PickerClose phone={phone}>Cancel</PickerClose>
          <button
            type="button"
            onClick={apply}
            className={cn(
              buttonRecipe.base,
              buttonRecipe.look.primary,
              buttonRecipe.size.sm,
            )}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export { DateRangePicker };
export { popoverAlign } from "./picker-shell";
export type { DateRangePickerProps };
